/**
 * @typedef {Object} UserAuth
 * @property {Number} organizationId
 * @property {String} internalId
 */

/**
 * Singleton for manipulating the single websocket connection we keep alive.
 */
class Socket {
  /**
   * Example usage: new Socket(require("socket.io-client"), REALTIME_URL))
   * @param io
   * @param url
   * @param logger
   */
  constructor(io, url, logger = console) {
    this.logger = logger;
    this.url = url;
    this._socket = io(url, { transports: ['websocket'] });

    /**
     * Whether we managed to connect to the server. Before emitting any events, however,
     * we need to make sure we are subscribed.
     * @type {boolean}
     */
    this.isConnected = false;

    /**
     * Whether the server has accepted our 'subscribe' request.
     * @type {boolean}
     */
    this.isSubscribed = false;

    this._eventListeners = [];

    this._setupLogging(url);
    this._setupWatchers();
  }

  /**
   * @private
   */
  _setupLogging() {
    this.on('connect', () => {
      this.logger.log(`Connected to ${this.url}`);
    });
    this.on('disconnect', () => {
      this.logger.log(`Disconnected from ${this.url}`);
    });
    this.on('subscription_accepted', () => {
      this.logger.log(`Subscription accepted`);
    });
  }

  /**
   * Setup the watchers that trigger the callbacks to manage the
   * isConnected and isSubscribed variables.
   * @private
   */
  _setupWatchers() {
    const callEventListeners = () => {
      this._eventListeners.forEach(listener =>
        listener({
          isConnected: this.isConnected,
          isSubscribed: this.isSubscribed,
        }),
      );
    };
    this.on('connect', () => {
      this.isConnected = true;
      callEventListeners();
    });
    this.on('disconnect', () => {
      this.isConnected = false;
      this.isSubscribed = false;
      callEventListeners();
    });
    this.on('subscription_accepted', () => {
      this.isSubscribed = true;
      callEventListeners();
    });
  }

  /**
   * Called when isConnected or isSubscribed changes
   * @param callback
   */
  addEventListener(callback) {
    this._eventListeners = [...this._eventListeners, callback];
    this.logger.log(
      `Socket added event listener (total ${this._eventListeners.length})`,
    );
  }

  /**
   * Use to remove an event listener registered with addEventListener.
   * @param callback
   */
  removeEventListener(callback) {
    this._eventListeners = this._eventListeners.filter(c => c !== callback);
    this.logger.log(
      `Socket removed event listener (total ${this._eventListeners.length})`,
    );
  }

  /**
   * Called exactly once as soon as we have an active connection.
   * Useful for subscribing to the socket.
   * @param {Function} callback
   */
  onceConnected(callback) {
    if (this.isConnected) callback();
    else {
      const handler = () => {
        callback();
        this.off('connect', handler);
      };
      this.on('connect', handler);
    }
  }

  /**
   * Called exactly once as soon as we receive the 'subscription_accepted' message.
   * If we had already received it (Socket.isSubscribed === true), then it's called immediately.
   * @param {Function} callback
   */
  onceSubscribed(callback) {
    if (this.isSubscribed) callback();
    else {
      const handler = () => {
        callback();
        this.off('subscription_accepted', handler);
      };
      this.on('subscription_accepted', handler);
    }
  }

  /**
   * @see onceConnected for connection events.
   * @param {String} message The message to observe.
   * @param {Function} callback The callback to be called once we receive the message.
   */
  on(message, callback) {
    this._socket.on(message, callback);
  }

  /**
   * Attempts to subscribe.
   * @param {UserAuth} auth
   * @param {Profile} profile
   */
  attemptSubscribe(auth, profile) {
    this.logger.log(
      `Attempting to subscribe with profile ${JSON.stringify(profile)}`,
    );
    if (!auth.organizationId) {
      throw new Error(`auth.organizationId is invalid or missing (found: ${auth.organizationId})`);
    }
    if (!auth.internalId) {
      throw new Error(`auth.internalId is invalid or missing (found: ${auth.internalId})`);
    }
    this._socket.emit('subscribe', { auth, profile });
  }

  emit(message, ...args) {
    this._socket.emit(message, ...args);
  }

  off(message, handler) {
    console.log(`Removed listener on ${message}`);
    this._socket.removeListener(message, handler);
  }

  offAll() {
    this._socket.removeAllListeners();
  }
}

module.exports = Socket;



// WEBPACK FOOTER //
// ../node_modules/@flic-chat/js-sdk/src/socket.js